import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, func, number, string } from 'prop-types';
import classNames from 'classnames';
import css from './RangeSlider.module.css';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const arraysEqual = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
const HANDLE_SIZE = 24;
const HANDLE_RADIUS = HANDLE_SIZE / 2;
const TRACK_HEIGHT = 2;

const RangeSlider = props => {
  const { rootClassName, className, handles, min, max, step, onChange, onDragStart, onDragEnd } = props;
  const rootClasses = classNames(rootClassName || css.root, className);
  const trackRef = useRef(null);
  const [activeHandle, setActiveHandle] = useState(null);
  const [internalHandles, setInternalHandles] = useState(handles);
  const lastSentRef = useRef(handles);
  const activeHandleRef = useRef(activeHandle);
  const rafIdRef = useRef(null);
  const pendingHandlesRef = useRef(null);

  useEffect(() => {
    activeHandleRef.current = activeHandle;
  }, [activeHandle]);

  useEffect(() => {
    if (activeHandleRef.current === null) {
      setInternalHandles(handles);
    }
    lastSentRef.current = handles;
  }, [handles]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  const flushPendingChange = useCallback(() => {
    rafIdRef.current = null;
    const next = pendingHandlesRef.current;
    if (!next || arraysEqual(next, lastSentRef.current)) {
      return;
    }
    lastSentRef.current = next;
    onChange(next);
  }, [onChange]);

  const range = max - min;
  const toPct = useCallback(
    value => {
      if (range <= 0) {
        return 0;
      }
      return ((value - min) / range) * 100;
    },
    [min, range]
  );

  const valueFromClientX = useCallback(
    clientX => {
      const trackEl = trackRef.current;
      if (!trackEl || range <= 0) {
        return min;
      }
      const rect = trackEl.getBoundingClientRect();
      if (rect.width <= 0) {
        return min;
      }
      const px = clamp(clientX - rect.left, 0, rect.width);
      const rawValue = min + (px / rect.width) * range;
      const snapped = Math.round(rawValue / step) * step;
      return clamp(snapped, min, max);
    },
    [max, min, range, step]
  );

  const setHandleValue = useCallback(
    (index, rawValue, emit = true) => {
      setInternalHandles(prev => {
        if (!prev.length || index < 0 || index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        const low = index === 0 ? min : next[index - 1];
        const high = index === next.length - 1 ? max : next[index + 1];
        const clamped = clamp(rawValue, low, high);
        if (next[index] === clamped) {
          return prev;
        }
        next[index] = clamped;
        if (emit) {
          pendingHandlesRef.current = next;
          if (rafIdRef.current === null) {
            rafIdRef.current = window.requestAnimationFrame(flushPendingChange);
          }
        }
        return next;
      });
    },
    [flushPendingChange, max, min]
  );

  const onGlobalMouseMove = useCallback(
    e => {
      const index = activeHandleRef.current;
      if (index === null) {
        return;
      }
      e.preventDefault();
      setHandleValue(index, valueFromClientX(e.clientX), true);
    },
    [setHandleValue, valueFromClientX]
  );

  const onGlobalTouchMove = useCallback(
    e => {
      const index = activeHandleRef.current;
      if (index === null) {
        return;
      }
      e.preventDefault();
      setHandleValue(index, valueFromClientX(e.touches[0].clientX), true);
    },
    [setHandleValue, valueFromClientX]
  );

  const stopDrag = useCallback(() => {
    if (rafIdRef.current !== null) {
      window.cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
      flushPendingChange();
    }
    setActiveHandle(null);
    onDragEnd();
  }, [flushPendingChange, onDragEnd]);

  useEffect(() => {
    if (activeHandle === null) {
      return undefined;
    }

    window.addEventListener('mousemove', onGlobalMouseMove, { passive: false });
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchmove', onGlobalTouchMove, { passive: false });
    window.addEventListener('touchend', stopDrag);
    window.addEventListener('touchcancel', stopDrag);

    return () => {
      window.removeEventListener('mousemove', onGlobalMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', onGlobalTouchMove);
      window.removeEventListener('touchend', stopDrag);
      window.removeEventListener('touchcancel', stopDrag);
    };
  }, [activeHandle, onGlobalMouseMove, onGlobalTouchMove, stopDrag]);

  const startDrag = useCallback(
    (index, clientX) => {
      setActiveHandle(index);
      onDragStart();
      setHandleValue(index, valueFromClientX(clientX), true);
    },
    [onDragStart, setHandleValue, valueFromClientX]
  );

  const sortedHandles = useMemo(() => internalHandles.slice(), [internalHandles]);

  return (
    <div className={rootClasses}>
      <div
        ref={trackRef}
        className={css.track}
        style={{
          left: `${HANDLE_RADIUS}px`,
          right: `${HANDLE_RADIUS}px`,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: `${TRACK_HEIGHT}px`,
            transform: 'translateY(-50%)',
            backgroundColor: '#e6e6e6',
          }}
        />

        {sortedHandles.slice(0, -1).map((h, idx) => {
          const left = toPct(h);
          const right = toPct(sortedHandles[idx + 1]);
          return (
            <div
              key={`range-${idx}`}
              style={{
                position: 'absolute',
                top: '50%',
                height: `${TRACK_HEIGHT}px`,
                transform: 'translateY(-50%)',
                left: `${left}%`,
                width: `${Math.max(0, right - left)}%`,
                backgroundColor: '#888',
              }}
            />
          );
        })}

        {sortedHandles.map((h, index) => {
          const isActive = activeHandle === index;
          return (
            <div
              key={`handle-${index}`}
              role="button"
              tabIndex={0}
              className={classNames({ [css.activeHandle]: isActive })}
              style={{
                position: 'absolute',
                top: '50%',
                left: `${toPct(h)}%`,
                width: `${HANDLE_SIZE}px`,
                height: `${HANDLE_SIZE}px`,
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                border: '2px solid #888',
                backgroundColor: '#fff',
                cursor: 'pointer',
                boxShadow: isActive ? '0 2px 12px 0 rgba(0, 0, 0, 0.3)' : 'none',
                zIndex: isActive ? 2 : 1,
              }}
              onMouseDown={e => {
                e.preventDefault();
                startDrag(index, e.clientX);
              }}
              onTouchStart={e => {
                e.preventDefault();
                startDrag(index, e.touches[0].clientX);
              }}
              onKeyDown={e => {
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  setHandleValue(index, h - step, true);
                } else if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  setHandleValue(index, h + step, true);
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

RangeSlider.defaultProps = {
  min: 0,
  max: 10000000,
  step: 1,
  rootClassName: null,
  className: null,
  onChange: () => null,
  onDragStart: () => null,
  onDragEnd: () => null,
};

RangeSlider.propTypes = {
  handles: arrayOf(number),
  min: number,
  max: number,
  step: number,
  rootClassName: string,
  className: string,
  onChange: func,
  onDragStart: func,
  onDragEnd: func,
};

export default RangeSlider;
